import React from 'react';
import { Button, Card, CardContent, CardHeader, CircularProgress, Modal, Paper } from "@material-ui/core";
import Fade from "@material-ui/core/Fade";
import { useSnackbar } from "notistack";
import { useMutation } from "@apollo/client";
import { UPLOAD_FILE } from "./fileQueries";

function isArrayElementReadOnly(array, index) {
    const descriptor = Object.getOwnPropertyDescriptor(array, index);
    return descriptor && descriptor.writable === false;
}

const FileUploadModal = ({
                             open,
                             setFileUploadOpen,
                             recordUpdate,
                             fileKey,
                             setFieldValue,
                             values,
                             rowInfo,
                             uploadType,
                             newTitle,
                         }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = React.useState(false);
    const [mutate] = useMutation(UPLOAD_FILE);

    return (
        <Modal
            open={open}
            onClose={() => setFileUploadOpen(!open)}
            aria-labelledby='simple-modal-title'
            aria-describedby='simple-modal-description'
            BackdropProps={{ invisible: true }}
        >
            <Fade in={open}>
                <Paper
                    style={{
                        position: 'absolute',
                        top: '30%',
                        left: '40%',
                        width: 400,
                    }}
                >
                    <Card>
                        <CardContent>
                            <CardHeader title="File Upload" />
                            <hr />
                            <div>
                                <Button
                                    variant="contained"
                                    component="label"
                                >
                                    {isLoading ? <CircularProgress /> : 'Upload File'}
                                    <input
                                        type="file"
                                        accept="image/*, .pdf"
                                        hidden
                                        onChange={async (event) => {
                                            const file = event.target.files[0];
                                            if (!file) return;

                                            setIsLoading(true);
                                            let updatedRecording = {};
                                            const fileExtension = file.name.split('.').pop();
                                            const uploadName = `${fileKey}.${fileExtension}`;

                                            try {

                                                await mutate({
                                                    variables: {
                                                        file,       // pass the actual File object
                                                        fileSize: `${file.size}`,
                                                        name: uploadName
                                                    }
                                                });

                                                // Handle different upload types
                                                if (uploadType === 'LAND') {
                                                    updatedRecording = await recordUpdate({
                                                        variables: { ...rowInfo, fileName: uploadName }
                                                    });
                                                } else if (uploadType === 'ROW') {
                                                    const { volPage, effectiveDate, dateFiled, comments, grantor, grantee } = rowInfo;
                                                    // Variable name must be `rowinfo` (lowercase i) to match the
                                                    // mutation's declared $rowinfo variable. Sending `rowInfo` leaves
                                                    // $rowinfo undefined on the server, the Cypher resolver writes
                                                    // null over the row's fields, and the upload silently fails.
                                                    const response = await recordUpdate({
                                                        variables: {
                                                            rowinfo: {
                                                                volPage,
                                                                effectiveDate,
                                                                dateFiled,
                                                                comments,
                                                                grantor,
                                                                grantee,
                                                                fileName: uploadName
                                                            },
                                                            ROWTitleID: rowInfo.id
                                                        }
                                                    });
                                                    // The mutation returns the whole ROW with its full titleRun array.
                                                    // Pull out the specific row we just updated so the local Formik
                                                    // splice below replaces it with a proper row shape (not the
                                                    // raw Apollo response wrapper).
                                                    const titleRun = response?.data?.updateROWTitle?.titleRun || [];
                                                    updatedRecording = titleRun.find((t) => t.id === rowInfo.id) || rowInfo;
                                                } else if (uploadType === 'UNIT') {
                                                    const {volPage, effectiveDate, dateFiled, comments, grantor, grantee, docType} = rowInfo;
                                                    const response = await recordUpdate({
                                                        variables: {
                                                            info: {
                                                                volPage,
                                                                effectiveDate,
                                                                dateFiled,
                                                                comments,
                                                                grantor,
                                                                grantee,
                                                                docType,
                                                                fileName: uploadName
                                                            },
                                                            UnitTitleID: rowInfo.id
                                                        }
                                                    });
                                                    const { data: { updateUnitTitle: titleRun } } = response;
                                                    updatedRecording = titleRun.titleRun[0];

                                                } else if (uploadType === 'LANDBOOK') {

                                                    const { dateFiled, dateUpdated, notes, id } = rowInfo;

                                                    const response = await recordUpdate({
                                                        tractID: rowInfo.tractID || rowInfo.id,
                                                        id,
                                                        fileName: uploadName,
                                                        dateFiled: dateFiled || '',
                                                        dateUpdated: dateUpdated || '',
                                                        notes: notes || ''
                                                    });
                                                    const { data: { UpdateLandBook: updatedLandBook } } = response;
                                                    updatedRecording = updatedLandBook;

                                                    const landBook = values.landBook || [];
                                                    const index = landBook.findIndex(lb => lb.id === rowInfo.id);
                                                    if (index !== -1) {
                                                        setFieldValue('landBook', [
                                                            ...landBook.slice(0, index),
                                                            updatedLandBook,
                                                            ...landBook.slice(index + 1),
                                                        ]);
                                                    }

                                                } else if (uploadType === 'TITLE') {
                                                    const response = await recordUpdate({
                                                        variables: {
                                                            id: rowInfo.id,
                                                            fileName: uploadName,
                                                            volPage: rowInfo.volPage || '',
                                                            book: rowInfo.book || '',
                                                            effectiveDate: rowInfo.effectiveDate || '',
                                                            dateFiled: rowInfo.dateFiled || '',
                                                            docType: rowInfo.docType || '',
                                                            comments: rowInfo.comments || '',
                                                            grantor: rowInfo.grantor || '',
                                                            grantee: rowInfo.grantee || '',
                                                            nsaTotal: rowInfo.nsaTotal || '',
                                                            nmaTotal: rowInfo.nmaTotal || '',
                                                            landDescription: rowInfo.landDescription || '',
                                                        },
                                                    });

                                                    const { data: { UpdateLandTitle } } = response;
                                                    updatedRecording = UpdateLandTitle;
                                                }

                                                // Existing logic for updating finalData array
                                                if (values.finalData) {
                                                    const { finalData } = values;
                                                    const index = finalData.findIndex(({ id }) => id === rowInfo.id);
                                                    if (isArrayElementReadOnly(finalData, index)) {
                                                        const updatedFormData = [
                                                            ...finalData.slice(0, index),
                                                            updatedRecording,
                                                            ...finalData.slice(index + 1)
                                                        ];
                                                        setFieldValue('finalData', updatedFormData);
                                                    } else {
                                                        finalData.splice(index, 1, updatedRecording);
                                                        setFieldValue('finalData', finalData);
                                                    }
                                                }

                                                enqueueSnackbar('File Successfully Uploaded', { variant: 'success' });
                                            } catch (e) {
                                                console.log(e);
                                                enqueueSnackbar('File Upload Failed', { variant: 'error' });
                                            } finally {
                                                setFileUploadOpen(!open);
                                                setIsLoading(false);
                                                setTimeout(() => closeSnackbar(), 1000);
                                            }
                                        }}
                                    />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </Paper>
            </Fade>
        </Modal>
    );
};

export default FileUploadModal;